import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { HomeShell } from "@/shell/HomeShell";
import { candidateApi } from "@/lib/candidateApi";
import { initialsFromName } from "@/lib/initials";
import type { VadSpeed } from "@/lib/types";

const VAD_OPTIONS: { value: VadSpeed; label: string; description: string }[] = [
  { value: "fast", label: "Fast", description: "Coach responds quickly after you finish speaking." },
  { value: "slow", label: "Measured", description: "A natural pause before the coach responds." },
  { value: "very_slow", label: "Extended", description: "Longer silence — more time to finish your thought." },
];

// Only one of these is real. The live interview speaks through Gemini Live,
// which is pinned to a single voice: gemini_live.py has `VOICE = "Puck"` as a
// module constant, run_live_session takes no voice argument, and nothing ever
// passes the candidate's choice into it. So whatever was picked here, every
// candidate has always heard the same American male voice. Offering four made
// the coach sound wrong to anyone who chose one of the other three.
//
// The unavailable ones are shown but disabled rather than deleted, so the
// intent stays visible and re-enabling one is a matter of wiring the voice
// through to run_live_session and flipping `available`.
const VOICE_OPTIONS = [
  { value: "american_male",   label: "American Male",   description: "Confident American accent.", available: true },
  { value: "british_female",  label: "British Female",  description: "Warm, professional British accent.", available: false },
  { value: "british_male",    label: "British Male",    description: "Clear, authoritative British accent.", available: false },
  { value: "american_female", label: "American Female", description: "Friendly American accent.", available: false },
];

// What the coach actually speaks in, regardless of what is stored on the
// profile. Existing candidates have british_female and similar saved from when
// all four looked selectable.
const EFFECTIVE_VOICE = "american_male";

export function Settings() {
  const { token = "" } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [displayName, setDisplayName] = useState("");
  const [vadSpeed, setVadSpeed] = useState<VadSpeed>("fast");
  const [notifyWeekly, setNotifyWeekly] = useState(true);
  const [preferredVoice, setPreferredVoice] = useState(EFFECTIVE_VOICE);

  // Email change (regression restore — see api_candidate.py email-change endpoints)
  const [email, setEmail] = useState("");
  const [emailStage, setEmailStage] = useState<"idle" | "editing" | "verifying">("idle");
  const [newEmail, setNewEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailNotice, setEmailNotice] = useState<string | null>(null);
  const [emailFallbackCode, setEmailFallbackCode] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Branding for shell
  const [agencyName, setAgencyName] = useState<string | undefined>(undefined);
  const [candidateName, setCandidateName] = useState<string | undefined>(undefined);
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);
  const [logoFilter, setLogoFilter] = useState<string | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  useEffect(() => {
    Promise.all([
      candidateApi.getInvite(token),
      candidateApi.getSettings(token),
    ]).then(([inv, settings]) => {
      setLoading(false);

      if (inv.ok) {
        setAgencyName(inv.agencyName);
        setCandidateName(inv.candidateName);
        setLogoUrl(inv.branding.logoUrl || undefined);
        setLogoFilter(inv.branding.logoFilter || undefined);
      }

      if (!settings.ok) {
        setError(settings.error ?? "Could not load settings.");
        return;
      }
      setEmail(settings.email ?? "");
      setDisplayName(settings.displayName ?? "");
      setVadSpeed(settings.vadSpeed ?? "fast");
      setNotifyWeekly(settings.notifyWeekly ?? true);
      // Coerce anything we cannot actually speak in. Candidates have
      // british_female and similar saved from when all four looked
      // selectable; without this the screen would show American Male
      // selected and then save the old value back.
      {
        const saved = settings.preferredVoice ?? EFFECTIVE_VOICE;
        const usable = VOICE_OPTIONS.some((o) => o.available && o.value === saved);
        setPreferredVoice(usable ? saved : EFFECTIVE_VOICE);
      }
    });
  }, [token]);

  async function handleStartEmailChange() {
    setEmailBusy(true);
    setEmailError(null);
    setEmailNotice(null);
    setEmailFallbackCode(null);
    const res = await candidateApi.startEmailChange(token, newEmail);
    setEmailBusy(false);
    if (!res.ok) {
      setEmailError(res.error ?? "Could not send verification code.");
      return;
    }
    setEmailNotice(`Code sent to ${newEmail}. Enter it below.`);
    setEmailFallbackCode(res.fallbackCode ?? null);
    setEmailStage("verifying");
  }

  async function handleVerifyEmailChange() {
    setEmailBusy(true);
    setEmailError(null);
    const res = await candidateApi.verifyEmailChange(token, newEmail, emailCode);
    setEmailBusy(false);
    if (!res.ok) {
      setEmailError(res.error ?? "Incorrect or expired code. Please try again.");
      return;
    }
    setEmail(res.email ?? newEmail);
    setEmailStage("idle");
    setNewEmail("");
    setEmailCode("");
    setEmailNotice("Email updated.");
    setEmailFallbackCode(null);
  }

  function handleCancelEmailChange() {
    setEmailStage("idle");
    setNewEmail("");
    setEmailCode("");
    setEmailError(null);
    setEmailNotice(null);
    setEmailFallbackCode(null);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setError(null);
    const res = await candidateApi.saveSettings(token, { displayName, vadSpeed, notifyWeekly, preferredVoice });
    setSaving(false);
    if (!res.ok) {
      setError(res.error ?? "Could not save settings.");
    } else {
      setSaved(true);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaved(false), 3000);
    }
  }

  async function downloadMyData() {
    if (!token) return;
    setExporting(true);
    setExportMsg(null);
    const r = await candidateApi.exportData(token);
    setExporting(false);
    if (!r.ok) {
      setExportMsg({
        ok: false,
        text: r.status === 403
          ? "Email verification required. Sign in with the code we email you, then try again."
          : r.error,
      });
      return;
    }
    const url = URL.createObjectURL(r.blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "interview-coach-my-data.json";
    a.click();
    URL.revokeObjectURL(url);
    setExportMsg({ ok: true, text: "Your data has been downloaded." });
  }

  return (
    <HomeShell
      agencyName={agencyName}
      candidateInitials={initialsFromName(displayName) || initialsFromName(candidateName)}
      logoUrl={logoUrl}
      logoFilter={logoFilter}
      activeTab="settings"
    >
      <div className="max-w-[520px]">
        <div className="mb-6">
          <p
            className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em]"
            style={{ color: "#7C7590" }}
          >
            Account
          </p>
          <h1 className="text-[22px] font-semibold mt-1" style={{ color: "#17131F" }}>
            Settings
          </h1>
        </div>

        {loading ? (
          <div className="flex items-center gap-2" style={{ color: "#7C7590" }}>
            <div
              className="w-4 h-4 rounded-full border-2 animate-spin"
              style={{ borderColor: "#B4AEC4", borderTopColor: "var(--accent)" }}
            />
            <span className="text-[13px]">Loading…</span>
          </div>
        ) : (
          <form onSubmit={handleSave} className="flex flex-col gap-5">
            {/* Profile */}
            <div
              className="rounded-[18px] border p-5"
              style={{ background: "#FFFFFF", borderColor: "#EFEBF7" }}
            >
              <p
                className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] mb-4"
                style={{ color: "#7C7590" }}
              >
                Profile
              </p>
              <label className="block">
                <span
                  className="font-mono text-[10px] uppercase tracking-[0.14em] mb-1.5 block"
                  style={{ color: "#7C7590" }}
                >
                  Display name
                </span>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your preferred first name"
                  maxLength={100}
                  className="w-full rounded-lg px-3.5 py-2.5 text-[14px] outline-none transition-shadow"
                  style={{
                    background: "#F3F1FA",
                    border: "1px solid #EFEBF7",
                    color: "#17131F",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "var(--accent)")}
                  onBlur={(e) => (e.target.style.borderColor = "#EFEBF7")}
                />
              </label>

              <div className="mt-4">
                <span
                  className="font-mono text-[10px] uppercase tracking-[0.14em] mb-1.5 block"
                  style={{ color: "#7C7590" }}
                >
                  Email address
                </span>

                {emailStage === "idle" && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[14px]" style={{ color: "#17131F" }}>{email}</span>
                    <button
                      type="button"
                      onClick={() => { setEmailStage("editing"); setEmailError(null); setEmailNotice(null); }}
                      className="text-[13px] font-semibold shrink-0"
                      style={{ color: "var(--accent)" }}
                    >
                      Change
                    </button>
                  </div>
                )}

                {emailStage === "editing" && (
                  <div className="flex flex-col gap-2">
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="new@example.com"
                      className="w-full rounded-lg px-3.5 py-2.5 text-[14px] outline-none"
                      style={{ background: "#F3F1FA", border: "1px solid #EFEBF7", color: "#17131F" }}
                    />
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        disabled={emailBusy || !newEmail}
                        onClick={handleStartEmailChange}
                        className="rounded-full px-4 py-1.5 text-[13px] font-semibold text-white disabled:opacity-50"
                        style={{ background: "#17131F" }}
                      >
                        {emailBusy ? "Sending…" : "Send code"}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEmailChange}
                        className="text-[13px]"
                        style={{ color: "#7C7590" }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {emailStage === "verifying" && (
                  <div className="flex flex-col gap-2">
                    <p className="text-[13px]" style={{ color: "#7C7590" }}>
                      Enter the 6-digit code sent to {newEmail}.
                    </p>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={emailCode}
                      onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="123456"
                      className="w-full rounded-lg px-3.5 py-2.5 text-[16px] font-mono tracking-[0.3em] outline-none"
                      style={{ background: "#F3F1FA", border: "1px solid #EFEBF7", color: "#17131F" }}
                    />
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        disabled={emailBusy || emailCode.length !== 6}
                        onClick={handleVerifyEmailChange}
                        className="rounded-full px-4 py-1.5 text-[13px] font-semibold text-white disabled:opacity-50"
                        style={{ background: "#17131F" }}
                      >
                        {emailBusy ? "Verifying…" : "Verify"}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEmailChange}
                        className="text-[13px]"
                        style={{ color: "#7C7590" }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {emailError && (
                  <p className="text-[12.5px] mt-1.5" style={{ color: "#E1573F" }}>{emailError}</p>
                )}
                {emailNotice && !emailError && (
                  <p className="text-[12.5px] mt-1.5" style={{ color: "#22A06B" }}>{emailNotice}</p>
                )}
                {emailFallbackCode && !emailError && (
                  <p className="text-[12.5px] mt-1" style={{ color: "#B08900" }}>
                    Email delivery failed — your code is{" "}
                    <span className="font-mono font-bold tracking-[3px]">{emailFallbackCode}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Response speed */}
            <div
              className="rounded-[18px] border p-5"
              style={{ background: "#FFFFFF", borderColor: "#EFEBF7" }}
            >
              <p
                className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] mb-1"
                style={{ color: "#7C7590" }}
              >
                Response Speed
              </p>
              <p className="text-[13px] mb-4" style={{ color: "#7C7590" }}>
                How quickly the coach responds after you stop speaking.
              </p>
              <div className="flex flex-col gap-2">
                {VAD_OPTIONS.map((opt) => {
                  const active = vadSpeed === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className="flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-colors"
                      style={{
                        background: active ? "var(--accent-50)" : "transparent",
                        border: `1px solid ${active ? "var(--accent)" : "#EFEBF7"}`,
                      }}
                    >
                      <input
                        type="radio"
                        name="vadSpeed"
                        value={opt.value}
                        checked={active}
                        onChange={() => setVadSpeed(opt.value)}
                        className="mt-0.5"
                        style={{ accentColor: "var(--accent)" }}
                      />
                      <div>
                        <p className="text-[14px] font-semibold" style={{ color: "#17131F" }}>
                          {opt.label}
                        </p>
                        <p className="text-[12px]" style={{ color: "#7C7590" }}>
                          {opt.description}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Coach Voice */}
            <div
              className="rounded-[18px] border p-5"
              style={{ background: "#FFFFFF", borderColor: "#EFEBF7" }}
            >
              <p
                className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] mb-1"
                style={{ color: "#7C7590" }}
              >
                Coach Voice
              </p>
              <p className="text-[13px] mb-4" style={{ color: "#7C7590" }}>
                More voices are on the way. For now your coach speaks in one voice.
              </p>
              <div className="flex flex-col gap-2">
                {VOICE_OPTIONS.map((opt) => {
                  // A stored preference for a voice we cannot actually speak in
                  // must not render as the chosen one, or the screen keeps
                  // making a promise the interview does not keep.
                  const active = opt.available && preferredVoice === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${
                        opt.available ? "cursor-pointer" : "cursor-not-allowed"
                      }`}
                      style={{
                        background: active ? "var(--accent-50)" : "transparent",
                        border: `1px solid ${active ? "var(--accent)" : "#EFEBF7"}`,
                        opacity: opt.available ? 1 : 0.45,
                      }}
                    >
                      <input
                        type="radio"
                        name="voice"
                        value={opt.value}
                        checked={active}
                        disabled={!opt.available}
                        onChange={() => opt.available && setPreferredVoice(opt.value)}
                        className="mt-0.5"
                        style={{ accentColor: "var(--accent)" }}
                      />
                      <div>
                        <p className="text-[14px] font-semibold" style={{ color: "#17131F" }}>
                          {opt.label}
                          {!opt.available && (
                            <span className="ml-2 font-mono text-[10px] font-normal uppercase tracking-[0.12em]"
                                  style={{ color: "#7C7590" }}>
                              Coming soon
                            </span>
                          )}
                        </p>
                        <p className="text-[12px]" style={{ color: "#7C7590" }}>
                          {opt.description}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Notifications */}
            <div
              className="rounded-[18px] border p-5"
              style={{ background: "#FFFFFF", borderColor: "#EFEBF7" }}
            >
              <p
                className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] mb-4"
                style={{ color: "#7C7590" }}
              >
                Notifications
              </p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyWeekly}
                  onChange={(e) => setNotifyWeekly(e.target.checked)}
                  className="mt-0.5 w-4 h-4"
                  style={{ accentColor: "var(--accent)" }}
                />
                <div>
                  <p className="text-[14px] font-medium" style={{ color: "#17131F" }}>
                    Weekly preparation reminder
                  </p>
                  <p className="text-[13px]" style={{ color: "#7C7590" }}>
                    Receive a weekly nudge to keep your preparation on track.
                  </p>
                </div>
              </label>
            </div>

            {error && (
              <p role="alert" className="text-[13px]" style={{ color: "#E1573F" }}>
                {error}
                {error === "Email verification required." && (
                  <> <Link className="underline" to={`/invite/${token}/verify`}>Verify your email to save account settings</Link></>
                )}
              </p>
            )}

            <div className="flex items-center gap-3 pb-2">
              <button
                type="submit"
                disabled={saving}
                className="rounded-full px-6 py-2.5 text-[14px] font-semibold text-white transition-opacity disabled:opacity-50"
                style={{ background: "#17131F" }}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
              {saved && (
                <span className="text-[13px] font-medium" style={{ color: "#22A06B" }}>
                  Saved
                </span>
              )}
            </div>
          </form>
        )}

        {/* Log out */}
        {!loading && (
          <div
            className="rounded-[18px] border p-5 mt-2"
            style={{ background: "#FFFFFF", borderColor: "#EFEBF7" }}
          >
            <p
              className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] mb-1"
              style={{ color: "#7C7590" }}
            >
              Session
            </p>
            <p className="text-[13px] mb-4" style={{ color: "#7C7590" }}>
              Sign out of your account on this device.
            </p>
            <a
              href="/candidate/logout"
              className="inline-flex items-center rounded-full px-5 py-2 text-[13px] font-semibold transition-opacity hover:opacity-80"
              style={{ background: "#17131F", color: "#fff" }}
            >
              Log out
            </a>
          </div>
        )}

        {/* GDPR danger zone */}
        {!loading && (
          <div
            className="rounded-[18px] border p-5 mt-2"
            style={{ background: "#FFFFFF", borderColor: "#EFEBF7" }}
          >
            <p
              className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] mb-1"
              style={{ color: "#E1573F" }}
            >
              Data & Privacy
            </p>
            <p className="text-[13px] mb-3" style={{ color: "#7C7590" }}>
              Download a copy of your invite details, settings, transcripts, scorecards and
              knowledge base as a JSON file.
            </p>
            <button
              type="button"
              onClick={downloadMyData}
              disabled={exporting}
              data-testid="settings-download-data"
              className="inline-flex items-center rounded-full border px-5 py-2 text-[13px] font-semibold transition-opacity hover:opacity-80 disabled:opacity-50"
              style={{ borderColor: "#EFEBF7", color: "#17131F" }}
            >
              {exporting ? "Preparing download..." : "Download my data"}
            </button>
            {exportMsg && (
              <p
                role="status"
                className="text-[13px] mt-2"
                style={{ color: exportMsg.ok ? "#2F7A4F" : "#E1573F" }}
                data-testid="settings-download-status"
              >
                {exportMsg.text}
              </p>
            )}
            <p className="text-[13px] mb-4 mt-5" style={{ color: "#7C7590" }}>
              Permanently deletes the transcripts, scorecards, job description and notes for this
              interview, and your CV unless another active invite under the same email still uses it.
              This cannot be undone.
            </p>
            <a
              href={`/invite/${token}/erase`}
              className="inline-flex items-center rounded-full px-5 py-2 text-[13px] font-semibold transition-opacity hover:opacity-80"
              style={{ background: "#E1573F", color: "#fff" }}
            >
              Delete my data (GDPR)
            </a>
          </div>
        )}
      </div>
    </HomeShell>
  );
}
